import React, { useState, useEffect } from 'react';
import { Plus, Shield, Crown, Users, Trash2, ToggleLeft, ToggleRight, CheckSquare, Square, RefreshCw, Edit2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../utils/api';
import Modal from '../components/Modal';
import { useAuth } from '../../context/AuthContext';

const ROLES = [
  { value: 'master_admin', label: 'Master Admin', color: '#B8A830', icon: Crown },
  { value: 'manager',      label: 'Web Manager',  color: '#4F8EF7', icon: Shield },
];

const MODULES = [
  { key: 'products',     label: 'Products',      actions: ['view','create','edit','delete'] },
  { key: 'collections',  label: 'Collections',   actions: ['view','create','edit','delete'] },
  { key: 'solutions',    label: 'Solutions',     actions: ['view','create','edit','delete'] },
  { key: 'content',      label: 'Content',       actions: ['view','create','edit','delete'] },
  { key: 'contacts',     label: 'Contacts',      actions: ['view','create','edit','delete'] },
  { key: 'submissions',  label: 'Submissions',   actions: ['view','edit','delete'] },
  { key: 'siteSettings', label: 'Site Settings', actions: ['view','edit'] },
  { key: 'homePage',     label: 'Home Page',     actions: ['view','edit'] },
  { key: 'aboutUs',      label: 'About Us',      actions: ['view','edit'] },
  { key: 'logo',         label: 'Logo',          actions: ['edit'] },
];

const emptyPerms = () => Object.fromEntries(
  MODULES.map(m => [m.key, Object.fromEntries(m.actions.map(a => [a, false]))])
);

const empty = { name: '', email: '', password: '', role: 'manager' };

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl">
      <p style={{ color: payload[0].payload.color }}>{payload[0].name}: <strong>{payload[0].value}</strong></p>
    </div>
  );
};

export default function AdminUsers() {
  const { isMasterAdmin } = useAuth();
  const [users, setUsers]         = useState([]);
  const [modal, setModal]         = useState(false);
  const [permModal, setPermModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [permUser, setPermUser]   = useState(null);
  const [form, setForm]           = useState(empty);
  const [perms, setPerms]         = useState(emptyPerms());
  const [loading, setLoading]     = useState(false);
  const [permLoad, setPermLoad]   = useState(false);

  const load = () => api.get('/users').then(r => setUsers(r.data.users || []));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit   = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: '', role: u.role }); setModal(true); };

  const openPerms = (u) => {
    setPermUser(u);
    setPerms(u.permissions ? { ...emptyPerms(), ...u.permissions } : emptyPerms());
    setPermModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const update = { name: form.name, role: form.role };
        if (form.password) update.password = form.password;
        await api.put(`/users/${editing._id}`, update);
        toast.success('User updated');
      } else {
        await api.post('/users', form);
        toast.success('User created');
      }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.delete(`/users/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u._id}`, { isActive: !u.isActive });
      toast.success(`User ${!u.isActive ? 'activated' : 'deactivated'}`);
      load();
    } catch { toast.error('Error'); }
  };

  const savePerms = async () => {
    setPermLoad(true);
    try {
      await api.put(`/users/${permUser._id}/permissions`, { permissions: perms });
      toast.success('Permissions saved');
      setPermModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setPermLoad(false); }
  };

  const togglePerm = (mod, action) => {
    setPerms(p => ({ ...p, [mod]: { ...p[mod], [action]: !p[mod]?.[action] } }));
  };

  const toggleAllModule = (mod) => {
    const m = MODULES.find(x => x.key === mod);
    const allOn = m.actions.every(a => perms[mod]?.[a]);
    setPerms(p => ({ ...p, [mod]: Object.fromEntries(m.actions.map(a => [a, !allOn])) }));
  };

  const grantAll = () => {
    const all = {};
    MODULES.forEach(m => { all[m.key] = Object.fromEntries(m.actions.map(a => [a, true])); });
    setPerms(all);
  };

  const revokeAll = () => setPerms(emptyPerms());

  /* chart data */
  const roleCounts = ROLES.map(r => ({
    name: r.label, value: users.filter(u => u.role === r.value).length, color: r.color,
  })).filter(r => r.value > 0);

  const activeCount   = users.filter(u => u.isActive).length;
  const inactiveCount = users.length - activeCount;

  const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[3];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-sm text-gray-400 mt-0.5">{users.length} total users · {activeCount} active</p>
        </div>
        {isMasterAdmin && (
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
            <Plus size={14} /> Add User
          </button>
        )}
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Users',   value: users.length,   color: '#4F8EF7', bg: '#EFF6FF' },
          { label: 'Active',        value: activeCount,    color: '#10B981', bg: '#F0FDF4' },
          { label: 'Inactive',      value: inactiveCount,  color: '#F43F5E', bg: '#FFF1F2' },
          { label: 'Managers',      value: users.filter(u => u.role === 'manager').length, color: '#8B5CF6', bg: '#F5F3FF' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-3xl font-bold text-gray-800">{s.value}</p>
            <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">{s.label}</p>
            <div className="mt-3 h-1 rounded-full" style={{ background: `${s.color}30` }}>
              <div className="h-1 rounded-full" style={{ background: s.color, width: `${users.length ? (s.value / users.length) * 100 : 0}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Chart + Table */}
      <div className="grid lg:grid-cols-4 gap-4">
        {/* Pie chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">By Role</p>
          {roleCounts.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={roleCounts} cx="50%" cy="50%" innerRadius={35} outerRadius={55}
                    paddingAngle={3} dataKey="value" stroke="none">
                    {roleCounts.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {roleCounts.map(r => (
                  <div key={r.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                      <span className="text-gray-500">{r.name}</span>
                    </div>
                    <span className="font-bold text-gray-700">{r.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-300 text-xs">No users yet</div>
          )}
        </div>

        {/* Users table */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800">All Users</p>
            <button onClick={load} className="text-gray-400 hover:text-gray-600 transition-colors">
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['User', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => {
                  const ri = getRoleInfo(u.role);
                  return (
                    <tr key={u._id} className="hover:bg-gray-50 transition-colors group">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-bold shrink-0"
                            style={{ background: `linear-gradient(135deg, ${ri.color}, ${ri.color}99)` }}>
                            {u.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
                          style={{ background: `${ri.color}15`, color: ri.color }}>
                          <ri.icon size={10} /> {ri.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full
                          ${u.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-emerald-500' : 'bg-red-400'}`} />
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-400">
                        {new Date(u.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3 px-4">
                        {isMasterAdmin && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEdit(u)} title="Edit"
                              className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors">
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => openPerms(u)} title="Permissions"
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                              <Shield size={13} />
                            </button>
                            <button onClick={() => toggleActive(u)} title={u.isActive ? 'Deactivate' : 'Activate'}
                              className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                              {u.isActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                            </button>
                            {u.role !== 'master_admin' && (
                              <button onClick={() => handleDelete(u._id)} title="Delete"
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-300 gap-2">
                <Users size={32} />
                <p className="text-sm">No users found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      {modal && (
        <Modal title={editing ? 'Edit User' : 'Create New User'} onClose={() => setModal(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                placeholder="e.g. John Smith"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
            </div>
            {!editing && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                  placeholder="user@example.com"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Password {editing && <span className="font-normal text-gray-400">(leave blank to keep current)</span>}
              </label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                required={!editing} placeholder={editing ? 'New password (optional)' : 'Min 6 characters'}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Role</label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.filter(r => r.value !== 'master_admin').map(r => (
                  <button key={r.value} type="button"
                    onClick={() => setForm({ ...form, role: r.value })}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left transition-all
                      ${form.role === r.value ? 'border-transparent' : 'border-gray-200 hover:border-gray-300'}`}
                    style={form.role === r.value ? { background: `${r.color}15`, borderColor: r.color } : {}}>
                    <r.icon size={14} style={{ color: r.color }} />
                    <span className="text-sm font-medium text-gray-700">{r.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
              {loading ? 'Saving…' : (editing ? 'Update User' : 'Create User')}
            </button>
          </form>
        </Modal>
      )}

      {/* Permissions Modal */}
      {permModal && permUser && (
        <Modal title={`Permissions — ${permUser.name}`} onClose={() => setPermModal(false)} wide>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">Toggle permissions for each module. Master Admin always has full access.</p>
              <div className="flex gap-2">
                <button onClick={grantAll} className="text-xs px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg font-medium hover:bg-emerald-100 transition-colors">
                  Grant All
                </button>
                <button onClick={revokeAll} className="text-xs px-3 py-1.5 bg-red-50 text-red-500 rounded-lg font-medium hover:bg-red-100 transition-colors">
                  Revoke All
                </button>
              </div>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {MODULES.map(mod => {
                const allOn = mod.actions.every(a => perms[mod.key]?.[a]);
                return (
                  <div key={mod.key} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <button onClick={() => toggleAllModule(mod.key)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900">
                        {allOn ? <CheckSquare size={15} className="text-yellow-500" /> : <Square size={15} className="text-gray-400" />}
                        {mod.label}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-5">
                      {mod.actions.map(action => {
                        const on = !!perms[mod.key]?.[action];
                        return (
                          <button key={action} onClick={() => togglePerm(mod.key, action)}
                            className={`text-xs px-3 py-1 rounded-lg font-medium capitalize transition-all border
                              ${on ? 'text-white border-transparent' : 'text-gray-400 border-gray-200 hover:border-gray-300'}`}
                            style={on ? { background: '#B8A830' } : {}}>
                            {action}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <button onClick={savePerms} disabled={permLoad}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
              {permLoad ? <><RefreshCw size={13} className="animate-spin" /> Saving…</> : 'Save Permissions'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
